import { NetworkStatus, useLazyQuery } from '@apollo/client';
import { GET_WORKPACKAGE_DETAIL_BY_IDS } from '@bechtel/client-gql';
import { Box, Button, ButtonSize, Heading, Icon, Spinner, SpinnerSize, Text } from '@bechtel/gds';
import { WorkPackage } from '@bechtel/types';
import { Trans } from '@lingui/macro';
import { concat, get } from 'lodash';
import { FC, Suspense, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { WorkPackageDetailHeader } from '~/components/organisms/work-package-detail-header/internals';
import { WorkPackageActions } from './actions';
import CommodityList from './commodity-list';
import { WorkPackageContext } from './context';
import { WorkPackageStats } from './stats';
import {
  InformationContainerStyled,
  LoaderStyled,
  NoCommoditiesFoundStyled,
  WorkPackageDetailContainer,
  WorkPackageDetailStyled,
} from './styled';
// @ts-ignore: tsc declarations not recognising types/index.d.ts
import { ReactComponent as EmptyStateComodities } from '@bechtel/client-assets/dist/images/empty-state-commodities.svg';
import { checkIfUnitOfMeasureIsHours, commodityPageSize, ErrorBoundary, useAppUser } from '@bechtel/client-common';
import { WorkPackageHeaderRowStyled } from '~/list/styled';
// @ts-ignore: tsc declarations not recognising types/index.d.ts
import { ReactComponent as PackageIcon } from '@bechtel/client-assets/dist/images/work-package.svg';

interface RouteParams {
  id: string;
}

const WorkPackagesDetail: FC = () => {
  const { id } = useParams<RouteParams>();
  const decodedId = id ? decodeURIComponent(id) : '';
  const containerRef = useRef<HTMLDivElement>(null);
  const [scrollLength, setScrollLength] = useState(commodityPageSize);
  const { user } = useAppUser();
  const isUnitOfMeasureHour = checkIfUnitOfMeasureIsHours(user);

  useEffect(() => {
    containerRef.current?.scrollTo(0, 0);
  }, []);

  const [loadWorkPackageDetail, { loading, error, data, refetch, networkStatus }] = useLazyQuery<{
    workPackagesByIds: WorkPackage[];
  }>(GET_WORKPACKAGE_DETAIL_BY_IDS);

  useEffect(() => {
    if (!decodedId) return;
    loadWorkPackageDetail({
      variables: {
        ids: [decodedId],
      },
    });
  }, [decodedId]);

  const workPackage = get(data, 'workPackagesByIds[0]');
  const workOwners = concat(workPackage?.primaryOwners, workPackage?.secondaryOwners, workPackage?.otherOwners);

  if (!decodedId) {
    return (
      <WorkPackageDetailContainer>
        <WorkPackageHeaderRowStyled>
          <Heading size="600" color="white" weight="600">
            <Trans> No Work Package Selected</Trans>
          </Heading>
        </WorkPackageHeaderRowStyled>
        <Box flexDirection={{ sm: 'column' }} alignItems={{ sm: 'center' }} pt={{ sm: '900' }} mt={{ sm: '500' }}>
          <PackageIcon />
          <Box mt={{ sm: '700' }} px={{ sm: '600' }}>
            <Text size="400" family="graphik" color="grey.600">
              <Trans> No work package details to show</Trans>
            </Text>
          </Box>
        </Box>
      </WorkPackageDetailContainer>
    );
  }

  if (loading || networkStatus === NetworkStatus.refetch) {
    return (
      <Box css="height: 100%;" data-testid="loading">
        <LoaderStyled>
          <Spinner size={SpinnerSize.LARGE} />
        </LoaderStyled>
      </Box>
    );
  }

  if (error) {
    return (
      <Box css="height: 100%;">
        <LoaderStyled>
          <Box pb={{ sm: '500' }} flexDirection={{ sm: 'column' }} alignItems={{ sm: 'center' }}>
            <Text>
              <Trans>There was a problem loading this Work Package.</Trans>
            </Text>
            <Text>
              <Trans>Please click the button below to try again.</Trans>
            </Text>
          </Box>
          <Box>
            <Button size={ButtonSize.LARGE} onClick={() => refetch()}>
              <Box mr={{ sm: '400' }}>
                <Icon icon="undo" />
              </Box>
              <Trans> Retry</Trans>
            </Button>
          </Box>
        </LoaderStyled>
      </Box>
    );
  }

  if (!data || !data.workPackagesByIds || !data.workPackagesByIds.length) {
    return (
      <Box css="height: 100%;">
        <LoaderStyled>
          <Box
            pb={{ sm: '500' }}
            flexDirection={{ sm: 'column' }}
            alignItems={{ sm: 'center' }}
            data-testid="invalid-work-package"
          >
            <Text>
              <Trans>A Work Package with this identifier does not exist.</Trans>
            </Text>
          </Box>
        </LoaderStyled>
      </Box>
    );
  }

  const handleScroll = (e) => {
    if (e.target.scrollTop + e.target.clientHeight + 200 >= e.target.scrollHeight) {
      setScrollLength(scrollLength + commodityPageSize);
    }
  };

  return (
    <WorkPackageContext
      id={workPackage.id}
      tag={workPackage.tag}
      startDate={workPackage.startDate}
      endDate={workPackage.endDate}
      facilities={workPackage.facilities}
      assignedCrewMembers={workOwners}
      primaryOwners={workPackage.primaryOwners}
      secondaryOwners={workPackage.secondaryOwners}
      otherOwners={workPackage.otherOwners}
    >
      <WorkPackageDetailContainer
        onScroll={(e) => {
          handleScroll(e);
        }}
        ref={containerRef}
      >
        <WorkPackageDetailStyled>
          <WorkPackageDetailHeader
            showSecondaryOwners={workPackage.secondaryOwners || workPackage.otherOwners ? true : false}
            workPackages={[workPackage]}
            showRemainingTime={true}
          />
          <InformationContainerStyled>
            <WorkPackageStats workPackage={workPackage} isUnitOfMeasureHour={isUnitOfMeasureHour} />
            <WorkPackageActions commodityTypes={workPackage.availableCommodityTypes} />
          </InformationContainerStyled>
          {workPackage.availableCommodityTypes.length === 0 ? (
            <Box
              justifyContent={{ sm: 'center' }}
              px={{ sm: '700' }}
              py={{ sm: '700' }}
              css={`
                ${({ theme }) => `background-color: ${theme.colors.grey[200]};`};
                flex: 1;
              `}
            >
              <NoCommoditiesFoundStyled>
                <EmptyStateComodities style={{ width: '135px', height: '145px' }}></EmptyStateComodities>
                <Text size={700} weight={600} color={'black'} family="graphik">
                  <Trans>No Tags Found</Trans>
                </Text>
              </NoCommoditiesFoundStyled>
            </Box>
          ) : (
            <Suspense fallback={<Spinner size={SpinnerSize.LARGE} />}>
              <ErrorBoundary>
                <CommodityList
                  workPackageId={decodedId}
                  availableCommodityTypes={workPackage.availableCommodityTypes}
                  scrollLength={scrollLength}
                  resetScroll={() => {
                    setScrollLength(commodityPageSize);
                  }}
                  pageSize={commodityPageSize}
                  isUnitOfMeasureHour={isUnitOfMeasureHour}
                />
              </ErrorBoundary>
            </Suspense>
          )}
        </WorkPackageDetailStyled>
      </WorkPackageDetailContainer>
    </WorkPackageContext>
  );
};

export default WorkPackagesDetail;
